import { useTranslation } from "react-i18next";
import LiaisonModel from "../../../../api/models/LiaisonModel";
import StudentModel from "../../../../api/models/StudentModel";
import TutorModel from "../../../../api/models/TutorModel";
import UserProfilePictureAvatar from "../../../avatar/UserProfilePictureAvatar";
import UserTypeBadge from "../../../badges/UserTypeBadge";
import {
  getQuraanProgressOptions,
  getSessionsProgressOptions,
  getStudentPieChartOptions,
  ProgressOption,
} from "./senderInfoUtils";
import PieChart from "../../../charts/PieCharts";
import Modal from "../../ModalWithFooter";
import "./senderinfo.scss";

type SenderInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  sender: StudentModel | TutorModel | LiaisonModel | any;
  senderType: "student" | "tutor" | "liaison" | "supervisor";
  sponsorShip: string;
  telawah?: string;
};
export default function SenderInfoModal(props: SenderInfoModalProps) {
  const {
    isOpen,
    onClose,
    sender,
    senderType,
    sponsorShip,
    telawah = "",
  } = props;
  const { t } = useTranslation("", {
    keyPrefix: "senderInfo",
  });

  return (
    <Modal open={isOpen} setOpen={onClose}>
      <Modal.Body setOpen={onClose} title={t("title")} withBackOnMobile>
        <UserProfilePictureAvatar
          profilePictureUrl={sender?.profile_picture_url}
          className="w-20 h-20"
          alt={senderType}
        />
        <div className="mt-3 text-xl leading-6 font-medium text-gray-600">
          {sender?.full_name ?? sender?.first_name + " " + sender?.last_name}
        </div>
        <div className="mt-2 text-sm font-normal text-gray-400">
          {sender?.account?.email}
        </div>
        <div className="mt-3 flex items-center gap-4">
          <UserTypeBadge userType={senderType} />
          {senderType === "student" && (
            <div className="tex-sm font-normal text-brandblue-500">
              {telawah}
            </div>
          )}
        </div>
        {senderType === "student" && (
          <InfoCard
            title={t("tutor")}
            value={(sender as StudentModel)?.tutor?.full_name || ""}
            className="mt-10"
          />
        )}
        {senderType === "tutor" && (
          // TODO: discuss with the backend how to get the tutor telawah
          <InfoCard title={t("telawahs")} value={telawah} className="mt-10" />
        )}
        <InfoCard
          title={t("sponsorship")}
          value={sponsorShip}
          className="mt-4"
        />
        {senderType === "student" && (
          <div className="mt-10 py-4 flex gap-6">
            <ProgressChartCard
              title={t("sessionsProgress")}
              options={getSessionsProgressOptions(sender as StudentModel)}
            />
            <ProgressChartCard
              title={t("quraanProgress")}
              options={getQuraanProgressOptions(sender as StudentModel)}
            />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

const ProgressChartCard = ({
  title,
  options,
}: {
  title: string;
  options: [ProgressOption, ProgressOption];
}) => {
  const option1 = options[0];
  const option2 = options[1];
  return (
    <div className="w-full flex flex-col gap-4">
      <h5 className="text-xs font-semibold text-gray-500">{title}</h5>
      <div className="grid grid-cols-[3.3rem_1fr] items-center gap-4">
        <PieChart
          options={getStudentPieChartOptions(
            [option1.name, option2.name],
            [option1.value, option2.value],
            [option1.color, option2.color]
          )}
          height={85}
        />
        <div className="w-full flex items-end">
          <span
            className="text-[28px] leading-[35px] font-medium"
            style={{ color: option1.color }}
          >
            {option1.value}
          </span>
          <span
            className="text-[15px] leading-6 font-medium"
            style={{ color: option2.color }}
          >
            /{option2.value}
          </span>
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({
  title,
  value,
  className,
}: {
  title: string;
  value: string;
  className: string;
}) => {
  return (
    <div className={`pb-3 border-b border-gray-200 ${className}`}>
      <h5 className="text-xs leading-5 font-medium text-gray-500">{title}</h5>
      <p className="text-[15px] leading-6 font-medium text-gray-700">{value}</p>
    </div>
  );
};
